/**
 * Coordinate helpers for the SW-corner ↔ insertion-point transform.
 *
 * Server-side ``output_data.trackers[i]`` carries ``sw_x``/``sw_y`` —
 * the south-west corner of the tracker bounding box in local UTM
 * meters. The WASM viewer renders each tracker as an
 * ``OdDbBlockReference`` whose insertion point is offset from the SW
 * corner by ``placement_offset[block_name]`` (= the block's local-
 * coords origin minus its SW vertex; locked per block-table record).
 *
 * Stage 10 introduced multiple block types (``108_INT``, ``81_EPN``,
 * ``INV_PAD``), so the offset is now keyed by block name. Pass the
 * full ``PlacementOffsetMap`` plus the entry's block name; the helpers
 * fall back to ``108_INT`` when the named entry is missing, then to
 * a zero offset if even ``108_INT`` isn't present (worst-case visual
 * misalignment, not a crash).
 */

import type { PlacementOffset, PlacementOffsetMap } from "../../types/layout";

const ZERO_OFFSET: PlacementOffset = { x: 0, y: 0 };

/**
 * Look up the offset for a given block name with sensible fallbacks.
 * Exposed so the viewer can cache the resolved offset per block.
 */
export function getPlacementOffset(
  offsets: PlacementOffsetMap,
  block: string,
): PlacementOffset {
  return offsets[block] ?? offsets["108_INT"] ?? ZERO_OFFSET;
}

/** SW-corner (server canonical) → block-ref insertion point (WASM world). */
export function swToInsertion(
  sw_x: number,
  sw_y: number,
  offsets: PlacementOffsetMap,
  block: string = "108_INT",
): { x: number; y: number } {
  const offset = getPlacementOffset(offsets, block);
  return { x: sw_x + offset.x, y: sw_y + offset.y };
}

/** Block-ref insertion point (WASM world) → SW-corner (server canonical). */
export function insertionToSw(
  point: { x: number; y: number },
  offsets: PlacementOffsetMap,
  block: string = "108_INT",
): { sw_x: number; sw_y: number } {
  const offset = getPlacementOffset(offsets, block);
  return { sw_x: point.x - offset.x, sw_y: point.y - offset.y };
}
